/**
 * Single source of truth for the v3 marketing-site color themes
 * (blue / yellow / red / purple / white) used across components/v3.
 *
 * Lives under components/v3 (not lib/) because tailwind.config.ts only
 * scans "./components/**" for literal class names — arbitrary-value
 * classes like `bg-[#006DFF]` are purged by Tailwind's JIT unless the
 * exact string appears in a file under a scanned content glob.
 */

export const THEME_NAMES = ["blue", "yellow", "red", "purple", "white"] as const;
export type ThemeName = (typeof THEME_NAMES)[number];

/** Canonical brand hex per theme, for inline style={{ color }} usage. */
export const THEME_HEX: Record<ThemeName, string> = {
  blue: "#006DFF",
  yellow: "#DEC068",
  red: "#C01610",
  purple: "#896deb",
  white: "#FFFFFF",
};

/** CustomButton.tsx primary variant. */
export const BUTTON_PRIMARY: Record<
  ThemeName,
  { colorClasses: string; arrowColor: string; borderClass: string }
> = {
  blue: { colorClasses: "bg-[#006DFF] hover:bg-[#0059e6] text-white", arrowColor: "#006DFF", borderClass: "border border-white" },
  yellow: { colorClasses: "bg-[#DEC068] hover:bg-[#c9ab56] text-[#252625]", arrowColor: "#252625", borderClass: "border border-[#252625]" },
  red: { colorClasses: "bg-[#C01610] hover:bg-[#a01210] text-white", arrowColor: "#C01610", borderClass: "border border-white" },
  purple: { colorClasses: "bg-[#896deb] hover:bg-[#6b66c6] text-white", arrowColor: "#896deb", borderClass: "border border-white" },
  white: { colorClasses: "bg-white hover:bg-white/90 text-v3-primary", arrowColor: "#006DFF", borderClass: "border border-v3-primary" },
};

/** CustomButton.tsx secondary/tertiary variant. */
export const BUTTON_SECONDARY: Record<ThemeName, string> = {
  blue: "border-[#006DFF]/30 bg-[#006DFF]/5 text-[#006DFF] hover:bg-[#006DFF]/10",
  yellow: "border-[#DEC068] bg-[#DEC068]/10 text-[#DEC068] hover:bg-[#DEC068]/20",
  red: "border-[#C01610] bg-[#C01610]/10 text-[#C01610] hover:bg-[#C01610]/20",
  purple: "border-[#896deb]/30 bg-[#896deb]/5 text-[#896deb] hover:bg-[#896deb]/10",
  white: "border-white bg-white/10 text-white hover:bg-white/20",
};

/** Shared by CallExpandBtn.tsx and YoutubeExpandBtnc.tsx ("dark" variant). */
export const EXPAND_BTN_DARK: Record<Exclude<ThemeName, "white">, string> = {
  blue: "border-[#006DFF] bg-[#006DFF]/10 text-[#006DFF]",
  yellow: "border-[#DEC068] bg-[#DEC068]/10 text-[#252625]",
  red: "border-[#C01610] bg-[#C01610]/10 text-[#C01610]",
  purple: "border-[#896deb] bg-[#896deb]/10 text-[#896deb]",
};

/** Shared by ScientificBenefits.tsx (iconColor) and ServiceSource.tsx (iconBg). */
export const ICON_ACCENT_TEXT: Record<Exclude<ThemeName, "white">, string> = {
  blue: "text-[#006DFF]",
  yellow: "text-[#D97706]",
  red: "text-[#DC2626]",
  purple: "text-[#A796E8]",
};

/** Branches.tsx per-group hover gradient. Purple intentionally uses a lighter shade than THEME_HEX.purple. */
export const BRANCH_GRADIENT: Record<Exclude<ThemeName, "white">, string> = {
  blue: "linear-gradient(90deg, rgba(0, 109, 255, 0.20) 0%, rgba(0, 109, 255, 0.00) 47.52%)",
  red: "linear-gradient(90deg, rgba(192, 22, 16, 0.20) 0%, rgba(192, 22, 16, 0.00) 47.52%)",
  yellow: "linear-gradient(90deg, rgba(222, 192, 104, 0.20) 0%, rgba(222, 192, 104, 0.00) 47.52%)",
  purple: "linear-gradient(90deg, rgba(167, 150, 232, 0.20) 0%, rgba(167, 150, 232, 0.00) 47.52%)",
};

/** GetSupport.tsx bottom-fade gradient. */
export const GET_SUPPORT_GRADIENT: Record<Exclude<ThemeName, "white">, string> = {
  blue: "linear-gradient(to top, #006DFF 0%, rgba(0, 109, 255, 0) 100%)",
  purple: "linear-gradient(to top, #896deb 0%, rgba(137, 109, 235, 0) 100%)",
  yellow: "linear-gradient(0deg, #9E8028 0%, rgba(158, 128, 40, 0.00) 100%)",
  red: "linear-gradient(0deg, #C01610 0%, rgba(192, 22, 16, 0.00) 100%)",
};

/** HowServiceHelp.tsx tint/border. Purple intentionally uses lighter shades than THEME_HEX.purple. */
export const HOW_SERVICE_TINT_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#006DFF0D",
  yellow: "#BEB5011A",
  red: "#7B00021A",
  purple: "#cdb5ff",
};

export const HOW_SERVICE_BORDER_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#006DFF",
  yellow: "#DEC068",
  red: "#C01610",
  purple: "#a796e8",
};

/** Footer.tsx copyright bar background (pastel tint). */
export const FOOTER_COPYRIGHT_BG_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#E4F0FF",
  yellow: "#FEF3C7",
  red: "#FEE2E2",
  purple: "#EDE9FE",
};

/** Footer.tsx copyright bar text color, paired with FOOTER_COPYRIGHT_BG_HEX. */
export const FOOTER_COPYRIGHT_TEXT_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#0251BB",
  yellow: "#92400E",
  red: "#991B1B",
  purple: "#5B21B6",
};

/** CTABanner.tsx dark card background + gradient overlay anchor color. */
export const CTA_BANNER_BASE_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#013275",
  yellow: "#451a03",
  red: "#450a0a",
  purple: "#2e1065",
};

/** CTABanner.tsx ambient glow accent (blurred circle behind the content). */
export const CTA_BANNER_GLOW_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#3f86ff",
  yellow: "#fcd34d",
  red: "#f87171",
  purple: "#a78bfa",
};

/** teams/[slug] profile photo bottom-fade gradient. */
export const TEAM_PROFILE_GRADIENT: Record<Exclude<ThemeName, "white">, string> = {
  blue: "linear-gradient(180deg, rgba(0, 109, 255, 0) 50%, rgba(0, 109, 255, 0.3) 100%)",
  yellow: "linear-gradient(180deg, rgba(222, 192, 104, 0) 50%, rgba(222, 192, 104, 0.3) 100%)",
  red: "linear-gradient(180deg, rgba(192, 22, 16, 0) 50%, rgba(192, 22, 16, 0.3) 100%)",
  purple: "linear-gradient(180deg, rgba(137, 109, 235, 0) 50%, rgba(137, 109, 235, 0.3) 100%)",
};

/** shared/CTABanner.tsx (in-article blog CTA) card gradient stops. */
export const BLOG_CTA_GRADIENT_HEX: Record<
  Exclude<ThemeName, "white">,
  { from: string; to: string }
> = {
  blue: { from: "#034FB6", to: "#013B89" },
  yellow: { from: "#9E8028", to: "#6B5518" },
  red: { from: "#C01610", to: "#7B0002" },
  purple: { from: "#896deb", to: "#5B21B6" },
};

/**
 * shared/CTABanner.tsx accent, used for text/icons sitting on white.
 * Yellow and purple are darkened versus THEME_HEX so they stay readable
 * against the white button and icon chips.
 */
export const BLOG_CTA_ACCENT_HEX: Record<Exclude<ThemeName, "white">, string> = {
  blue: "#006DFF",
  yellow: "#9E8028",
  red: "#C01610",
  purple: "#6B4FD8",
};
